import { useEffect, useState } from "react";
import styled from "styled-components";
import fontSize from "../../theme/fonts/FontTheme";
import DefaultInputBar from "../input/DefaultInputBar";
import H2 from "../typography/H2";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  h2 {
    font-size: ${fontSize.l};
  }
`;
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 20px;
`;
const Flex = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
`;
const ErrorLabel = styled.span`
  color: #9c2727;
  font-size: ${fontSize.xs};
  margin-top: 4px;
`;
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

function ContactForm({ setData, isValid }) {
  const [customerDetails, setCustomerDetails] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });

  const [shippingAddress, setShippingAddress] = useState({
    street: "",
    city: "",
    province: "",
    address: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    street: "",
    city: "",
    province: "",
    address: "",
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    street: false,
    city: false,
    province: false,
    address: false,
  });

  useEffect(() => {
    setData({
      customerDetails,
      shippingAddress,
    });
    isValid(validateForm());
  }, [customerDetails, shippingAddress, setData]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!validateEmail(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else if (!validatePhone(value)) {
          error = "Please enter a valid phone number";
        }
        break;
      case "firstName":
        if (!value) {
          error = "First name is required";
        } else if (value.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value) {
          error = "Last name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "address":
        if (!value) {
          error = "Address is required";
        } else if (value.length < 5) {
          error = "Address must be at least 5 characters";
        }
        break;
      case "province":
        if (!value) {
          error = "Province is required";
        }
        break;
      case "city":
        if (!value) {
          error = "City is required";
        }
        break;
      case "street":
        if (!value) {
          error = "Street is required";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validateField("firstName", customerDetails.firstName),
      lastName: validateField("lastName", customerDetails.lastName),
      phone: validateField("phone", customerDetails.phone),
      email: validateField("email", customerDetails.email),
      street: validateField("street", shippingAddress.street),
      city: validateField("city", shippingAddress.city),
      province: validateField("province", shippingAddress.province),
      address: validateField("address", shippingAddress.address),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleInputChange = (setter, field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));

    // Validate field if it has been touched before
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  return (
    <Wrapper>
      <InputGroup>
        <H2>Contact</H2>
        <InputContainer>
          <DefaultInputBar
            placeholder="Enter Email"
            type="email"
            value={customerDetails.email}
            onChange={(e) =>
              handleInputChange(setCustomerDetails, "email", e.target.value)
            }
            onBlur={() => handleBlur("email")}
            hasError={!!errors.email}
          />
          {errors.email && <ErrorLabel>{errors.email}</ErrorLabel>}
        </InputContainer>
      </InputGroup>

      <InputGroup>
        <H2>Delivery</H2>
        <Flex>
          <InputContainer>
            <DefaultInputBar
              placeholder="First Name"
              type="text"
              value={customerDetails.firstName}
              onChange={(e) =>
                handleInputChange(
                  setCustomerDetails,
                  "firstName",
                  e.target.value
                )
              }
              onBlur={() => handleBlur("firstName")}
              hasError={!!errors.firstName}
            />
            {errors.firstName && <ErrorLabel>{errors.firstName}</ErrorLabel>}
          </InputContainer>
          <InputContainer>
            <DefaultInputBar
              placeholder="Last Name"
              type="text"
              value={customerDetails.lastName}
              onChange={(e) =>
                handleInputChange(
                  setCustomerDetails,
                  "lastName",
                  e.target.value
                )
              }
              onBlur={() => handleBlur("lastName")}
              hasError={!!errors.lastName}
            />
            {errors.lastName && <ErrorLabel>{errors.lastName}</ErrorLabel>}
          </InputContainer>
        </Flex>

        <InputContainer>
          <DefaultInputBar
            placeholder="Main Address, Ex Naxal Bhatbhateni, Behind building A"
            type="text"
            value={shippingAddress.address}
            onChange={(e) =>
              handleInputChange(setShippingAddress, "address", e.target.value)
            }
            onBlur={() => handleBlur("address")}
            hasError={!!errors.address}
          />
          {errors.address && <ErrorLabel>{errors.address}</ErrorLabel>}
        </InputContainer>

        <Flex>
          <InputContainer>
            <DefaultInputBar
              placeholder="Province"
              type="text"
              value={shippingAddress.province}
              onChange={(e) =>
                handleInputChange(
                  setShippingAddress,
                  "province",
                  e.target.value
                )
              }
              onBlur={() => handleBlur("province")}
              hasError={!!errors.province}
            />
            {errors.province && <ErrorLabel>{errors.province}</ErrorLabel>}
          </InputContainer>
          <InputContainer>
            <DefaultInputBar
              placeholder="City"
              type="text"
              value={shippingAddress.city}
              onChange={(e) =>
                handleInputChange(setShippingAddress, "city", e.target.value)
              }
              onBlur={() => handleBlur("city")}
              hasError={!!errors.city}
            />
            {errors.city && <ErrorLabel>{errors.city}</ErrorLabel>}
          </InputContainer>
          <InputContainer>
            <DefaultInputBar
              placeholder="Street"
              type="text"
              value={shippingAddress.street}
              onChange={(e) =>
                handleInputChange(setShippingAddress, "street", e.target.value)
              }
              onBlur={() => handleBlur("street")}
              hasError={!!errors.street}
            />
            {errors.street && <ErrorLabel>{errors.street}</ErrorLabel>}
          </InputContainer>
        </Flex>

        <InputContainer>
          <DefaultInputBar
            placeholder="Enter Phone Number"
            type="text"
            value={customerDetails.phone}
            onChange={(e) =>
              handleInputChange(setCustomerDetails, "phone", e.target.value)
            }
            onBlur={() => handleBlur("phone")}
            hasError={!!errors.phone}
          />
          {errors.phone && <ErrorLabel>{errors.phone}</ErrorLabel>}
        </InputContainer>
      </InputGroup>
    </Wrapper>
  );
}

export default ContactForm;
